/**
 * 交易 API 模块
 *
 * 包含用户端资源激活、订单创建等交易接口
 */

import { request } from '@/api/instance'
import type { ApiResponse, PaginationResponse } from '@/types/response'
import { createIdempotencyKey } from '@/utils/payment/payment'
import type {
  MembershipOrderCreateParams,
  OrderPaymentParams,
  PaymentCallbackParams,
  PaymentCallbackResult,
  PaymentOrderResult,
  CoursePackageOrderCreateParams,
  CoursePackageOrderResult,
  PointsPurchaseOrderCreateParams,
  PointsDebtRecoveryOrderCreateParams,
  ResourceOrderCreateParams,
  ResourceOrderResult,
  ActivationCodeRedeemParams,
  ActivationCodeRedeemResult,
  OrderListParams,
} from '@/types/trade'

/** 创建 365 年度会员订单 */
export function createMembershipOrder(
  params: MembershipOrderCreateParams,
  idempotencyKey = createIdempotencyKey('membership')
) {
  return request.post<ApiResponse<PaymentOrderResult>>('/trade/orders/membership/', params, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
}

/** 创建知识点资源激活订单 */
export function createResourceOrder(
  params: ResourceOrderCreateParams,
  idempotencyKey = createIdempotencyKey('resource')
) {
  return request.post<ApiResponse<ResourceOrderResult>>('/trade/orders/resource/', params, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
}

/** 创建课包激活订单 */
export function createCoursePackageOrder(
  params: CoursePackageOrderCreateParams,
  idempotencyKey = createIdempotencyKey('course-package')
) {
  return request.post<ApiResponse<CoursePackageOrderResult>>(
    '/trade/orders/course-package/',
    params,
    { headers: { 'Idempotency-Key': idempotencyKey } }
  )
}

/** 创建积分购买订单 */
export function createPointsPurchaseOrder(
  params: PointsPurchaseOrderCreateParams,
  idempotencyKey = createIdempotencyKey('points')
) {
  return request.post<ApiResponse<PaymentOrderResult>>('/trade/orders/points-purchase/', params, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
}

/** 查询当前用户订单详情（支付宝支付后轮询确认到账用） */
export function getOrderDetail(orderNo: string) {
  return request.get<ApiResponse<PaymentOrderResult>>(`/trade/orders/${orderNo}/`)
}

/** 为当前用户的待支付支付宝订单重新生成收银台参数。 */
export function continueOrderPayment(orderNo: string, params: OrderPaymentParams) {
  return request.post<ApiResponse<PaymentOrderResult>>(`/trade/orders/${orderNo}/pay/`, params)
}

/** 查询匿名债务恢复订单状态；凭证只放在 POST body，不进入 URL 或日志。 */
export function getDebtRecoveryOrderStatus(orderNo: string, statusToken: string) {
  return request.post<ApiResponse<PaymentOrderResult>>(
    '/trade/orders/points-purchase/debt-recovery/status/',
    { order_no: orderNo, status_token: statusToken },
    { skipAuth: true, skipTokenRefresh: true }
  )
}

/** 获取当前用户订单列表，用于订单中心和生命周期状态刷新。 */
export function getOrderList(params?: OrderListParams) {
  return request.get<ApiResponse<PaginationResponse<PaymentOrderResult>>>('/trade/orders/', {
    params,
  })
}

/** 取消仍未支付的当前用户订单。 */
export function cancelOrder(
  orderNo: string,
  params?: { reason?: string },
  idempotencyKey = createIdempotencyKey('cancel')
) {
  return request.post<ApiResponse<PaymentOrderResult>>(
    `/trade/orders/${orderNo}/cancel/`,
    params ?? {},
    {
      headers: { 'Idempotency-Key': idempotencyKey },
    }
  )
}

/** 提交已支付订单的退款申请。 */
export function requestOrderRefund(
  orderNo: string,
  params: { reason: string },
  idempotencyKey = createIdempotencyKey('refund')
) {
  return request.post<ApiResponse<PaymentOrderResult>>(`/trade/orders/${orderNo}/refund/`, params, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
}

/** 为积分债务账号创建短信验证充值订单 */
export function createPointsDebtRecoveryOrder(
  params: PointsDebtRecoveryOrderCreateParams,
  idempotencyKey = createIdempotencyKey('debt-recovery')
) {
  return request.post<ApiResponse<PaymentOrderResult>>(
    '/trade/orders/points-purchase/debt-recovery/',
    params,
    {
      skipAuth: true,
      skipTokenRefresh: true,
      headers: { 'Idempotency-Key': idempotencyKey },
    }
  )
}

/** 提交微信 mock 支付成功回调 */
export function submitWechatPaymentCallback(params: PaymentCallbackParams) {
  return request.post<PaymentCallbackResult>('/trade/callback/wechat/', params, {
    skipAuth: true,
    skipTokenRefresh: true,
  })
}

/** 提交支付宝 mock 支付成功回调 */
export function submitAlipayPaymentCallback(params: PaymentCallbackParams) {
  return request.post<PaymentCallbackResult>('/trade/callback/alipay/', params, {
    skipAuth: true,
    skipTokenRefresh: true,
  })
}

/** 兑换激活码 */
export function redeemActivationCode(params: ActivationCodeRedeemParams) {
  return request.post<ApiResponse<ActivationCodeRedeemResult>>(
    '/trade/activation-codes/redeem/',
    params
  )
}

/** 兑换前预览激活码内容（不消耗） */
export function previewActivationCode(code: string) {
  return request.post<ApiResponse<ActivationCodeRedeemResult>>('/trade/activation-codes/preview/', {
    code,
  })
}
